import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMobile } from '../../hooks/useMobile';
import type { MemberRole } from '../../contexts/AuthContext';
import './AdminRoute.css';

interface AdminRouteProps {
  children: React.ReactNode;
  allowedRoles?: MemberRole[];
}

/**
 * Protected route that requires moderator or higher role
 * By default allows: moderator, admin, super_admin
 *
 * [UPDATED v0.10.0] Added mobile device protection
 * - Blocks mobile and tablet users from accessing admin pages
 * - Shows friendly "Please use desktop" message
 */
const AdminRoute = ({ children, allowedRoles }: AdminRouteProps) => {
  const { isAuthenticated, isLoading, hasRole } = useAuth();
  const { isPhone, isTablet } = useMobile();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login with the current location as redirect parameter
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // [NEW v0.10.0] Block mobile/tablet users
  if (isPhone || isTablet) {
    return (
      <div className="admin-mobile-blocked">
        <div className="admin-mobile-blocked__container">
          <div className="admin-mobile-blocked__icon">💻</div>
          <h1 className="admin-mobile-blocked__title">請使用桌面版</h1>
          <p className="admin-mobile-blocked__message">
            管理後台僅支援桌面版瀏覽器訪問
          </p>
          <p className="admin-mobile-blocked__hint">
            請使用電腦或平板（橫屏）訪問以獲得最佳體驗
          </p>
          <button
            className="admin-mobile-blocked__button"
            onClick={() => window.location.href = '/'}
          >
            返回首頁
          </button>
        </div>
      </div>
    );
  }

  // Default allowed roles for admin routes
  const defaultAllowedRoles: MemberRole[] = ['moderator', 'admin', 'super_admin'];
  const rolesToCheck = allowedRoles || defaultAllowedRoles;

  if (!hasRole(rolesToCheck)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
